
import React, { useState } from 'react';

interface IndustryDetail {
  label: string;
  desc: string;
  fullContent: {
    challenge: string;
    solutions: string[];
    benefit: string;
  };
  icon: React.ReactNode;
}

const TargetAudience: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const industries: IndustryDetail[] = [
    { 
      label: "Comercios", 
      desc: "Optimización de stock y ventas multi-canal.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 9l1-5h16l1 5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 9v10a2 2 0 002 2h14a2 2 0 002-2V9" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="9" y="13" width="6" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 9V3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      fullContent: {
        challenge: "Fragmentación de ventas entre local físico, WhatsApp y e-commerce.",
        solutions: [
          "Sincronización de stock en tiempo real entre todos los canales.",
          "Panel único de facturación y control de caja.",
          "Módulo de fidelización de clientes automático.",
          "Integración con sistemas de pago locales (Mercado Pago, etc)."
        ],
        benefit: "Eliminación de ventas sin stock y reducción del 30% en tiempo administrativo."
      }
    },
    { 
      label: "Emprendimientos", 
      desc: "Escalabilidad técnica desde el día 1.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="15" y="3" width="4" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.2"/>
        </svg>
      ),
      fullContent: {
        challenge: "Crecimiento desordenado y dependencia de procesos manuales lentos.",
        solutions: [
          "Desarrollo de MVP escalable (Mínimo Producto Viable).",
          "Automatización de cobros y suscripciones.",
          "Dashboard de métricas clave (Burn rate, LTV, CAC).",
          "Estructura en la nube de bajo costo y alta disponibilidad."
        ],
        benefit: "Capacidad de triplicar la base de clientes sin aumentar el equipo de soporte."
      }
    },
    { 
      label: "Servicios", 
      desc: "Gestión inteligente de turnos y clientes.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="16" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="7" y="14" width="2" height="2" rx="0.5" fill="currentColor" fillOpacity="0.4"/>
          <rect x="11" y="14" width="2" height="2" rx="0.5" fill="currentColor" fillOpacity="0.4"/>
          <rect x="15" y="14" width="2" height="2" rx="0.5" fill="currentColor" fillOpacity="0.4"/>
        </svg>
      ),
      fullContent: {
        challenge: "Ausentismo de clientes y desorden en la agenda diaria.",
        solutions: [
          "Sistema de turnos online con recordatorios por WhatsApp.",
          "Ficha digital de cliente con historial completo.",
          "Cobro de señas automático para garantizar la asistencia.",
          "App interna para prestadores con liquidación de comisiones."
        ],
        benefit: "Reducción del 50% en inasistencias y agenda 100% optimizada."
      }
    },
    { 
      label: "Logística", 
      desc: "Trazabilidad, ruteo y control total.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 8l-9-4-9 4 9 4 9-4z" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 8v8l9 4 9-4V8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 12v8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 10.5l5 2.5 5-2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      fullContent: {
        challenge: "Rutas ineficientes y falta de visibilidad del estado de los envíos.",
        solutions: [
          "Optimización de rutas por GPS para ahorro de combustible.",
          "App de choferes con hoja de ruta y firma digital.",
          "Seguimiento en tiempo real para el cliente final.",
          "Gestión de depósitos y cross-docking digital."
        ],
        benefit: "Ahorro del 20% en costos operativos y clientes más informados."
      }
    },
    { 
      label: "Gastronomía", 
      desc: "Comandas y mesas 100% digitales.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 12c0-5 4-9 9-9s9 4 9 9" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 12h18v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2z" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 19v2M9 21h6" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="10" y="7" width="4" height="2" rx="0.5" fill="currentColor" fillOpacity="0.2"/>
        </svg>
      ),
      fullContent: {
        challenge: "Errores en pedidos y lentitud en el servicio durante horas pico.",
        solutions: [
          "Menú QR con pedido y pago directo desde la mesa.",
          "Pantallas de cocina (KDS) que eliminan la comanda de papel.",
          "Control de recetas e insumos para evitar mermas.",
          "Integración con Apps de delivery (Rappi/PedidosYa)."
        ],
        benefit: "Rotación de mesas más rápida y control absoluto del costo de materia prima."
      }
    },
    { 
      label: "Salud", 
      desc: "Historias clínicas y agendas seguras.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.29 1.51 4.04 3 5.5l7 7 7-7z" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 8v4M10 10h4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      fullContent: {
        challenge: "Dificultad para acceder a datos históricos y riesgos de privacidad.",
        solutions: [
          "Historia clínica electrónica bajo normativas de seguridad.",
          "Agenda médica multi-profesional sincronizada.",
          "Telemedicina integrada con sala de espera digital.",
          "Prescripción digital y validación con obras sociales."
        ],
        benefit: "Atención más humana y eficiente con datos protegidos al 100%."
      }
    },
    { 
      label: "Profesionales", 
      desc: "Automatización de marca y gestión de leads.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 14c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="18" y="4" width="3" height="3" rx="1" fill="currentColor" fillOpacity="0.3"/>
        </svg>
      ),
      fullContent: {
        challenge: "Pérdida de tiempo en captación y seguimiento manual de prospectos.",
        solutions: [
          "Landing Pages de alta conversión para servicios.",
          "Funnel de ventas automático con email marketing.",
          "Gestión de documentos y contratos digitales.",
          "Pasarela de pagos internacionales (Stripe/PayPal)."
        ],
        benefit: "Un sistema de ventas que trabaja mientras vos te enfocás en tu profesión."
      }
    },
    { 
      label: "Distribuidoras", 
      desc: "Venta mayorista y ruteo estratégico.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 3v18M18 3v18" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="9" y="8" width="6" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.2"/>
        </svg>
      ),
      fullContent: {
        challenge: "Preventistas con poca información y logística de entrega caótica.",
        solutions: [
          "App para preventistas con catálogo y stock offline.",
          "Portal B2B para que los clientes compren directo.",
          "Algoritmos de ruteo para flota propia.",
          "Análisis de rotación de productos por zona."
        ],
        benefit: "Aumento del 15% en el ticket promedio y despachos sin errores."
      }
    }
  ];

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className="py-24 border-t border-white/5 relative">
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row items-end justify-between gap-8 mb-20">
          <div className="max-w-2xl">
            <div className="inline-block px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">
              Público Objetivo
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6">Expertos en <span className="text-white">dar orden</span></h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              Pensado para pymes que quieren crecer en serio. Hacé clic en tu rubro para ver cómo transformamos el caos en control real.
            </p>
          </div>
          <button 
            onClick={() => window.location.hash = '#soluciones'}
            className="group px-8 py-4 glass rounded-xl text-white font-bold hover:bg-white/10 transition-all border border-white/10 flex items-center gap-3"
          >
            Ver casos de éxito
            <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all">→</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((item, i) => {
            const isExpanded = expandedIndex === i;
            return (
              <div 
                key={i} 
                onClick={() => handleToggle(i)}
                className={`group relative p-8 glass rounded-[2.5rem] border transition-all duration-700 ease-in-out cursor-pointer overflow-hidden ${
                  isExpanded 
                    ? 'lg:col-span-2 lg:row-span-2 border-white/30 bg-white/10 shadow-[0_0_50px_rgba(255,255,255,0.05)]' 
                    : 'border-white/5 hover:border-white/20 hover:-translate-y-2'
                }`}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 border border-white/10 shadow-inner ${
                      isExpanded ? 'bg-red-600 text-white rotate-0 shadow-red-500/50' : 'bg-white/5 text-white group-hover:scale-110 group-hover:rotate-6'
                    }`}>
                      {item.icon}
                    </div>
                    {isExpanded && (
                      <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">Solución Detallada</span>
                    )}
                  </div>
                  
                  <h3 className={`font-bold text-white mb-2 transition-all ${isExpanded ? 'text-3xl' : 'text-xl'}`}>
                    {item.label}
                  </h3>
                  
                  <p className={`text-gray-400 transition-all ${isExpanded ? 'text-lg mb-8' : 'text-sm mb-4 line-clamp-2'}`}>
                    {item.desc}
                  </p>

                  <div className={`transition-all duration-700 overflow-hidden ${
                    isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="space-y-8 py-6 border-t border-white/10">
                      <div>
                        <h4 className="text-red-500 font-bold text-xs uppercase tracking-widest mb-3">El Desafío</h4>
                        <p className="text-gray-200 text-sm leading-relaxed">{item.fullContent.challenge}</p>
                      </div>

                      <div>
                        <h4 className="text-blue-400 font-bold text-xs uppercase tracking-widest mb-3">Nuestra Implementación</h4>
                        <ul className="space-y-3">
                          {item.fullContent.solutions.map((sol, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-sm text-gray-400">
                              <span className="w-1.5 h-1.5 rounded-full bg-white/20 mt-1.5" />
                              {sol}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                        <h4 className="text-green-400 font-bold text-xs uppercase tracking-widest mb-2">Resultado Final</h4>
                        <p className="text-white text-sm font-medium">{item.fullContent.benefit}</p>
                      </div>
                    </div>
                  </div>

                  {!isExpanded && (
                    <div className="mt-auto pt-4 flex items-center gap-2 text-[10px] font-black text-gray-500 uppercase tracking-widest">
                      Ver solución <span className="text-white">→</span>
                    </div>
                  )}
                </div>

                {/* Decorative glows */}
                {isExpanded && (
                  <>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/5 blur-[100px] rounded-full pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/5 blur-[100px] rounded-full pointer-events-none" />
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Closing CTA inside Section */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 text-sm italic mb-4">¿No encontrás tu industria? Desarrollamos soluciones a medida para cualquier modelo de negocio complejo.</p>
          <a href="#contacto" className="text-white font-bold border-b border-white/20 pb-1 hover:border-white transition-all">Consultar por un rubro específico</a>
        </div>
      </div>
    </section>
  );
};

export default TargetAudience;
